import { ThemeProvider, createTheme, darken, getContrastRatio } from '@mui/material/styles';
import { PropsWithChildren } from 'react';
import useLocalStorage from '../../hooks/use-local-storage';
import { SWIFTFOX_GRADIENTS, SWIFTFOX_PALETTE } from '../../palette';
import { darkTheme } from '../../themes/dark';
import { lightTheme } from '../../themes/light';
import { muiButtonStyleOverrides } from './button-overrides';
import { muiButtonGroupStyleOverrides } from './button-group-overrides';
import { createChipPalette } from './chip-palette';
import { muiDialogStyleOverrides } from './dialog-overrides';
import { muiOutlinedInputStyleOverrides } from './input-overrides';
import { muiListItemButtonStyleOverrides, muiMenuItemStyleOverrides, muiMenuListStyleOverrides } from './menu-item-overrides';
import { muiSnackbarContentStyleOverrides } from './snackbar-overrides';

const SELECTED_THEME_STORE = 'SELECTED_THEME';

const THEME_MAP = {
	light: lightTheme,
	dark: darkTheme
};

const lightenAmount = 0.2;
const dataColours: { colour: string }[] = [
	{ colour: darken('#27B0B9', lightenAmount) },
	{ colour: darken('#EB6321', lightenAmount) },
	{ colour: darken('#F43535', lightenAmount) },
	{ colour: darken('#64C279', lightenAmount) },
	{ colour: darken('#F07922', lightenAmount) },
	{ colour: darken('#176629', lightenAmount) },
	{ colour: darken('#3A66FF', lightenAmount) },
	{ colour: darken('#512AC1', lightenAmount) },
	{ colour: darken('#A98DF9', lightenAmount) },
	{ colour: darken('#7C9078', lightenAmount) },
	{ colour: darken('#8E8E8E', lightenAmount) }
];

const DataColors: Record<string, string> = {};

const chooseContrast = (main: string) =>
	getContrastRatio(main, SWIFTFOX_PALETTE.neutral['10-almostWhite']) > 4.5
		? SWIFTFOX_PALETTE.neutral['10-almostWhite']
		: SWIFTFOX_PALETTE.neutral['70-nearlyBlack'];

interface SwiftFoxThemeProviderProps {
	/** Tenant-specific branding. The brand colour is used as the primary palette accent when provided. */
	clientTheme?: {
		colors?: {
			brand?: string;
		};
	};
	/** Force a specific colour mode, bypassing the user's stored preference and system setting. Useful for contexts like public forms that should always render in light mode. */
	mode?: 'light' | 'dark';
}

export const SwiftFoxThemeProvider = ({ children, clientTheme, mode }: PropsWithChildren<SwiftFoxThemeProviderProps>) => {
	const [storedThemeId, setStoredThemeId] = useLocalStorage(SELECTED_THEME_STORE, 'system');
	const systemThemeId =
		typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
			? 'light'
			: 'light'; // TODO: WHEN DARK MODE IS READY, CHANGE TO `dark : light`
	const selectedThemeId = mode ?? (storedThemeId === 'system' ? systemThemeId : storedThemeId);
	const selectedTheme = THEME_MAP[selectedThemeId] || lightTheme;

	const shadow = selectedTheme.palette.elevation.shadow;

	const theme = createTheme({
		...selectedTheme,
		logo: 'https://static.sfox.au/assets/logo.svg',
		fullLogo: 'https://static.sfox.au/assets/fulllogo.svg',
		colorSchemes: selectedThemeId,
		gradients: SWIFTFOX_GRADIENTS,
		colors: {
			dataColours: dataColours.map(d => d.colour),
			brand: clientTheme?.colors?.brand ?? SWIFTFOX_PALETTE.blue['40-coreBlue']
		},
		palette: {
			...selectedTheme.palette,
			_components: {
				...selectedTheme.palette._components,
				chip: createChipPalette(selectedTheme.palette)
			},
			swiftfox: SWIFTFOX_PALETTE,
			background: {
				...selectedTheme.palette.background,
				paper: selectedTheme.palette.background['paper-elevation-1']
			},
			blue: {
				main: SWIFTFOX_PALETTE.blue['40-coreBlue'],
				light: SWIFTFOX_PALETTE.blue['30-skywave'],
				dark: SWIFTFOX_PALETTE.blue['50-deepOcean'],
				contrastText: chooseContrast(SWIFTFOX_PALETTE.blue['40-coreBlue'])
			},
			orange: {
				main: SWIFTFOX_PALETTE.orange['30-highlight'],
				light: SWIFTFOX_PALETTE.orange['20-goldenSand'],
				dark: SWIFTFOX_PALETTE.orange['40-emberBaze'],
				contrastText: chooseContrast(SWIFTFOX_PALETTE.orange['30-highlight'])
			},
			neutral: {
				main: SWIFTFOX_PALETTE.neutral['50-midGrey'],
				light: SWIFTFOX_PALETTE.neutral['30-lightGrey'],
				dark: SWIFTFOX_PALETTE.neutral['70-nearlyBlack'],
				contrastText: chooseContrast(SWIFTFOX_PALETTE.neutral['50-midGrey'])
			},
			green: {
				main: SWIFTFOX_PALETTE.green['40-meadowLight'],
				light: SWIFTFOX_PALETTE.green['30-springGreen'],
				dark: SWIFTFOX_PALETTE.green['50-freshMeadow'],
				contrastText: chooseContrast(SWIFTFOX_PALETTE.green['40-meadowLight'])
			},
			magenta: {
				main: SWIFTFOX_PALETTE.magenta['40-crimsonRose'],
				light: SWIFTFOX_PALETTE.magenta['30-roseQuartz'],
				dark: SWIFTFOX_PALETTE.magenta['50-berryWine'],
				contrastText: chooseContrast(SWIFTFOX_PALETTE.magenta['40-crimsonRose'])
			},
			purple: {
				main: SWIFTFOX_PALETTE.purple['40-royalBloom'],
				light: SWIFTFOX_PALETTE.purple['30-orchidGlow'],
				dark: SWIFTFOX_PALETTE.purple['50-amethyst'],
				contrastText: chooseContrast(SWIFTFOX_PALETTE.purple['40-royalBloom'])
			},
			teal: {
				main: SWIFTFOX_PALETTE.teal['40-blueReef'],
				light: SWIFTFOX_PALETTE.teal['30-crystalBlue'],
				dark: SWIFTFOX_PALETTE.teal['50-oceanTeal'],
				contrastText: chooseContrast(SWIFTFOX_PALETTE.teal['40-blueReef'])
			},
			circles: {
				main: SWIFTFOX_PALETTE.circles.dark,
				light: SWIFTFOX_PALETTE.circles.light,
				dark: darken(SWIFTFOX_PALETTE.circles.dark, 0.2),
				contrastText: chooseContrast(SWIFTFOX_PALETTE.circles.dark)
			},
			roles: {
				main: SWIFTFOX_PALETTE.roles.dark,
				light: SWIFTFOX_PALETTE.roles.light,
				dark: darken(SWIFTFOX_PALETTE.roles.dark, 0.2),
				contrastText: chooseContrast(SWIFTFOX_PALETTE.roles.dark)
			}
		},
		shadows: [
			'none',
			...Array(3).fill(`0px 3px 6px -2px ${shadow['alpha-7']}, 0px 5px 15px 2px ${shadow['alpha-4']}, 0px -2px 10px 2px ${shadow['alpha-1']}`),
			...Array(5).fill(
				`0px 6px 12px -4px ${shadow['alpha-8']}, 0px 10px 30px 4px ${shadow['alpha-5']}, 0px -2px 10px 2px ${shadow['alpha-2']}`
			),
			...Array(16).fill(
				`0px 9px 18px -6px ${shadow['alpha-9']}, 0px 15px 45px 6px ${shadow['alpha-6']}, 0px -2px 10px 2px ${shadow['alpha-3']}`
			)
		],
		shape: {
			'radius-xs': 4,
			'radius-sm': 8,
			'radius-md': 12,
			'height-chip-sm': 24,
			'height-chip-md': 32,
			'height-chip-lg': 36,
			'height-icon-md': 20,
			'height-xs': 30,
			'height-sm': 36,
			'height-md': 42,
			'radius-lg': 20,
			'height-lg': 56,
			'radius-xl': 32,
			'radius-xxl': 48,
			'stroke-default': 1,
			'stroke-focused': 1.5,
			'app-bar-height-single': 64,
			'app-bar-height-double': 144,
			'radius-text-highlight': 4,
			'padding-text-highlight': 2
		},
		spacing: [0, 4, 8, 16, 24, 32, 40, 48, 64, 80, 96],
		breakpoints: {
			values: {
				xxs: 0,
				xs: 600,
				sm: 750,
				md: 900,
				lg: 1200,
				xl: 1536
			}
		},
		id: selectedThemeId,
		changeThemeId: (id: string) => setStoredThemeId(id),
		addDataColour: (type: string, value: string) => (DataColors[type] = value),
		getDataColour: (type: string) => DataColors[type] ?? SWIFTFOX_PALETTE.neutral['70-nearlyBlack'],
		typography: {
			fontFamily: 'Satoshi, Helvetica, Arial, sans-serif'
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: `
					@font-face {
						font-family: 'Satoshi';
						font-style: normal;
						font-display: swap;
						font-weight: 400;
						src: local('Satoshi'), local('Satoshi-Regular'), url(https://static.sfox.au/assets/Satoshi-Regular.woff2) format('woff2');
					}
					@font-face {
						font-family: 'Satoshi';
						font-style: normal;
						font-display: swap;
						font-weight: 500;
						src: local('Satoshi'), local('Satoshi-Medium'), url(https://static.sfox.au/assets/Satoshi-Medium.woff2) format('woff2');
					}
					@font-face {
						font-family: 'Satoshi';
						font-style: normal;
						font-display: swap;
						font-weight: 700;
						src: local('Satoshi'), local('Satoshi-Bold'), url(https://static.sfox.au/assets/Satoshi-Bold.woff2) format('woff2');
					}
					@font-face {
						font-family: 'HK Grotesk';
						font-style: normal;
						font-display: swap;
						font-weight: 400;
						src: local('HKGrotesk'), local('HKGrotesk-Regular'), url(https://static.sfox.au/assets/HKGrotesk-Regular.woff2) format('woff2');
					}
					@font-face {
						font-family: 'HK Grotesk';
						font-style: normal;
						font-display: swap;
						font-weight: 700;
						src: local('HKGrotesk'), local('HKGrotesk-Bold'), url(https://static.sfox.au/assets/HKGrotesk-Bold.woff2) format('woff2');
					}
					@font-face {
						font-family: 'HK Grotesk';
						font-style: normal;
						font-display: swap;
						font-weight: 900;
						src: local('HKGrotesk'), local('HKGrotesk-Black'), url(https://static.sfox.au/assets/HKGrotesk-Black.woff2) format('woff2');
					}
					:root {
						color-scheme: ${selectedTheme.palette.mode};
					}
				`
			},
			MuiButton: {
				styleOverrides: muiButtonStyleOverrides
			},
			MuiButtonGroup: {
				styleOverrides: muiButtonGroupStyleOverrides
			},
			MuiOutlinedInput: {
				styleOverrides: muiOutlinedInputStyleOverrides
			},
			MuiDialog: {
				styleOverrides: muiDialogStyleOverrides
			},
			MuiMenuItem: {
				styleOverrides: muiMenuItemStyleOverrides
			},
			MuiMenuList: {
				styleOverrides: muiMenuListStyleOverrides
			},
			MuiListItemButton: {
				styleOverrides: muiListItemButtonStyleOverrides
			},
			MuiSnackbarContent: {
				styleOverrides: muiSnackbarContentStyleOverrides
			},
			MuiTypography: {
				styleOverrides: {
					h1: { fontFamily: 'HK Grotesk, Helvetica, Arial, sans-serif', fontSize: '2rem', fontWeight: 700 },
					h2: { fontFamily: 'HK Grotesk, Helvetica, Arial, sans-serif', fontSize: '1.8rem', fontWeight: 700 },
					h3: { fontFamily: 'HK Grotesk, Helvetica, Arial, sans-serif', fontSize: '1.7rem', fontWeight: 700 },
					h4: { fontFamily: 'HK Grotesk, Helvetica, Arial, sans-serif', fontSize: '1.4rem', fontWeight: 700 },
					h5: { fontFamily: 'HK Grotesk, Helvetica, Arial, sans-serif', fontSize: '1.2rem', fontWeight: 700 },
					h6: { fontSize: '1rem', fontWeight: 500 }
				}
			}
		}
	});

	return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
